import type { ChartDataDigest } from './turn-outcome-digest';

/**
 * Collects per-widget chart-data digests for a freshly generated custom page,
 * completing once every expected widget has reported (or a timeout elapses).
 *
 * The drawer arms a collector after a page-create stream completes; the chart
 * components (mounted inside the CustomDash page tree, NOT the drawer) report
 * their settled digests as their data providers resolve. On completion the
 * drawer narrates the page from the collected digests.
 */
/** A deadline tier: complete `atMs` after arming IFF `minReported` digests settled. */
export type NarrationDeadlineTier = {
  atMs: number;
  minReported: number;
};

export class NarrationCollector {
  private readonly digestsByItemId = new Map<string, ChartDataDigest>();
  private readonly expected: Set<string>;
  private completed = false;
  private readonly timer: ReturnType<typeof setTimeout>;
  private readonly tierTimers: Array<ReturnType<typeof setTimeout>> = [];
  private readonly onComplete: (digests: ChartDataDigest[]) => void;

  constructor(params: {
    expectedItemIds: string[];
    timeoutMs: number;
    onComplete: (digests: ChartDataDigest[]) => void;
    /**
     * Early-completion tiers, each firing `atMs` after arming and completing
     * IFF at least `minReported` digests have settled by then. Lets a cold
     * page narrate from whatever arrived at a middle deadline instead of
     * riding the hard `timeoutMs` backstop. The legacy single-soft-deadline
     * params below are normalized into one tier.
     */
    deadlineTiers?: NarrationDeadlineTier[];
    // Back-compat: one soft deadline; `softMinReported` defaults to a
    // majority of the expected widgets.
    softTimeoutMs?: number;
    softMinReported?: number;
  }) {
    this.expected = new Set(params.expectedItemIds);
    this.onComplete = params.onComplete;
    this.timer = setTimeout(() => this.complete(), params.timeoutMs);
    let tiers: NarrationDeadlineTier[] = [];
    if (params.deadlineTiers) {
      tiers = [...params.deadlineTiers];
    } else if (params.softTimeoutMs !== undefined) {
      tiers = [
        {
          atMs: params.softTimeoutMs,
          minReported:
            params.softMinReported ??
            Math.max(1, Math.ceil(params.expectedItemIds.length / 2)),
        },
      ];
    }
    for (const tier of tiers) {
      this.tierTimers.push(
        setTimeout(() => {
          if (this.digestsByItemId.size >= tier.minReported) {
            this.complete();
          }
        }, tier.atMs)
      );
    }
  }

  report(itemId: string, digest: ChartDataDigest): void {
    if (this.completed || !this.expected.has(itemId)) return;
    this.digestsByItemId.set(itemId, digest);
    if (this.digestsByItemId.size >= this.expected.size) {
      this.complete();
    }
  }

  private clearTimers(): void {
    clearTimeout(this.timer);
    for (const t of this.tierTimers) clearTimeout(t);
  }

  private complete(): void {
    if (this.completed) return;
    this.completed = true;
    this.clearTimers();
    this.onComplete([...this.digestsByItemId.values()]);
  }

  dispose(): void {
    this.completed = true;
    this.clearTimers();
  }
}

// Module-level registry instead of a React context. The collector is owned by
// a ref inside the long-lived drawer, while the reporting chart components live
// in the CustomDash page tree (a separate subtree the drawer does not wrap), so
// there is no shared provider to thread a context value through.
//
// Tradeoff: a module-level value does NOT trigger React re-renders, so the
// reporter cannot "wake up" when a collector is armed. This is acceptable here
// because the page-create flow arms the collector BEFORE the new page's data
// providers settle (arming happens on stream completion, navigation + data
// fetch follow), so each reporter's own status/data effect fires after arming
// and reads the live collector. The 10s collector timeout covers any race where
// data settled before arming.
let activeCollector: NarrationCollector | null = null;

export function setActiveNarrationCollector(
  c: NarrationCollector | null
): void {
  activeCollector = c;
}

export function getActiveNarrationCollector(): NarrationCollector | null {
  return activeCollector;
}
